import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Stack, Tooltip } from '@mui/material';
import {
  AccessRequestSubmittedClientResponse,
  ResourceFilterType,
  useCreateUserAccessRequest,
  useDryRunCreateUserAccessRequest,
  useListIntegrations,
} from '@client-app-sdk';
import { LoadingButton } from '@mui/lab';
import { PermissionCombobox } from '@NewRequest/components/PermissionCombobox';
import { IntegrationCombobox } from '@NewRequest/components/IntegrationCombobox';
import { ResourceTypeCombobox } from '@NewRequest/components/ResourceTypeCombobox';
import { ResourceCombobox } from '@NewRequest/components/ResourceCombobox';
import { toast } from 'react-toastify';
import LabeledTextField from '@common/ui/LabeledTextField';
import { useAnalyticsContext } from '@common/utils/analytics/Provider';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import TemplateCTA from '@NewRequest/organisms/TemplateCTA';
import { FormState } from '@MyRequests/helpers';
import { ComboboxOption } from '@common/ui/Combobox/Combobox';
import { MultiplePermissionCombobox } from '@NewRequest/components/MultiplePermissionCombobox';
import { createAccessRequestClientModel, isFormFullExcludingJustification } from '@NewRequest/utils/helpers';

const initialFormState: FormState = {
  selectedIntegration: null,
  selectedResourceType: null,
  selectedPermission: null,
  selectedMultiplePermission: [],
  selectedResource: [],
  justification: '',
};

export interface AccessRequestFormProps {
  initFormState?: FormState;
  onSuccess: (accessRequestResponse?: AccessRequestSubmittedClientResponse) => void;
  onClickBack?: () => void;
  templatesEnabled?: boolean;
  disabledInputs?: boolean;
  sessionId?: string;
  showAlert?: boolean;
  dryRun?: boolean;
}

const FIELD_MISSING = 'FIELD_MISSING';
const JUSTIFICATION = 'justification';

export function AccessRequestForm({
  initFormState = initialFormState,
  onSuccess,
  onClickBack,
  templatesEnabled,
  disabledInputs,
  sessionId,
  showAlert,
  dryRun = true,
}: AccessRequestFormProps) {
  const [formState, setFormState] = useState<FormState>(initFormState);
  const [isMultiplePermission, setIsMultiplePermission] = useState(false);
  const { track } = useAnalyticsContext();
  const { data, isFetched } = useListIntegrations({ limit: 1 });
  const hasIntegrations = !isFetched || !!data?.pages[0]?.data.length;
  const {
    dryRunCreateUserAccessRequest,
    isLoading: isDryRunLoading,
    data: dryRunResponse,
    isSuccess: isDryRunSuccess,
  } = useDryRunCreateUserAccessRequest();
  const [isJustificationRequired, setIsJustificationRequired] = useState(true);
  const updateFormState = (field: keyof FormState, value: ComboboxOption | ComboboxOption[] | string | null): void => {
    setFormState((prevState) => {
      let newFormState: FormState = { ...prevState, [field]: value };
      switch (field) {
        case 'selectedIntegration':
          newFormState = {
            ...newFormState,
            selectedResourceType: null,
            selectedResource: [],
            selectedPermission: null,
            selectedMultiplePermission: [],
          };
          break;
        case 'selectedResourceType':
          newFormState = {
            ...newFormState,
            selectedResource: [],
            selectedPermission: null,
            selectedMultiplePermission: [],
          };
          break;
        default:
          break;
      }
      if (dryRun && isFormFullExcludingJustification(newFormState) && field !== 'justification')
        dryRunCreateUserAccessRequest({ createAccessRequestClientModel: createAccessRequestClientModel(formState) });
      return newFormState;
    });
  };

  const isUsingSearchTermInResources =
    formState.selectedResource?.some((option) => option.value.includes(ResourceFilterType.SearchTerm)) ?? false;

  const {
    createUserAccessRequest,
    isLoading,
    isSuccess,
    isError,
    data: accessRequestResponse,
  } = useCreateUserAccessRequest();

  useEffect(() => {
    if (isSuccess) {
      toast.success('Access request created successfully');
      track(AnalyticsEvents.AccessRequestSent);
      onSuccess(accessRequestResponse);
    }
    if (isError) {
      toast.error('Error creating access request');
      track(AnalyticsEvents.AccessRequestFailed);
    }
    if (isDryRunSuccess) {
      setIsJustificationRequired(
        dryRunResponse?.errors.some((error) => error.code === FIELD_MISSING && error.field === JUSTIFICATION) ?? true,
      );
    }
  }, [isSuccess, isError, track, onSuccess, accessRequestResponse, isDryRunSuccess, dryRunResponse?.errors]);

  const handleRequest = async () => {
    if (!isFormFullExcludingJustification(formState)) return;
    const modelWithJustification = {
      ...createAccessRequestClientModel(formState),
      justification: formState.justification,
    };
    createUserAccessRequest({ createAccessRequestClientModel: modelWithJustification });
  };

  const isFormDisabled =
    !isFormFullExcludingJustification(formState) ||
    isDryRunLoading ||
    (isJustificationRequired && !formState.justification);

  return (
    <Stack spacing={2}>
      {(!hasIntegrations || showAlert) && (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Uh oh! You don’t have any resources to request</AlertTitle>
          If you require access to specific resources, talk to your manager or admin.
          {<br />}
          Then, come back and request access.
        </Alert>
      )}
      <IntegrationCombobox
        sessionId={sessionId}
        value={formState.selectedIntegration}
        onChange={(value) => updateFormState('selectedIntegration', value)}
        disabled={formState.selectedIntegration?.value !== undefined && disabledInputs}
      />
      {formState.selectedIntegration?.value ? (
        <ResourceTypeCombobox
          sessionId={sessionId}
          value={formState.selectedResourceType}
          setIsMultiplePermission={setIsMultiplePermission}
          integrationId={formState.selectedIntegration.value}
          onChange={(value) => updateFormState('selectedResourceType', value)}
          disabled={formState.selectedResourceType?.value !== undefined && disabledInputs}
        />
      ) : (
        <LabeledTextField disabled label="Resource type" placeholder="Select resource type" />
      )}
      {formState.selectedIntegration?.value && formState.selectedResourceType?.value ? (
        <ResourceCombobox
          sessionId={sessionId}
          value={formState.selectedResource}
          integrationId={formState.selectedIntegration.value}
          resourceTypeId={formState.selectedResourceType.value}
          onChange={(value) => updateFormState('selectedResource', value)}
        />
      ) : (
        <LabeledTextField disabled label="Resources" placeholder="Select resources" />
      )}
      {formState.selectedIntegration?.value && formState.selectedResourceType?.value ? (
        isMultiplePermission ? (
          <MultiplePermissionCombobox
            sessionId={sessionId}
            value={formState.selectedMultiplePermission}
            integrationId={formState.selectedIntegration.value}
            resourceTypeId={formState.selectedResourceType.value}
            onChange={(value) => updateFormState('selectedMultiplePermission', value)}
          />
        ) : (
          <PermissionCombobox
            sessionId={sessionId}
            value={formState.selectedPermission}
            integrationId={formState.selectedIntegration.value}
            resourceTypeId={formState.selectedResourceType.value}
            onChange={(value) => updateFormState('selectedPermission', value)}
          />
        )
      ) : (
        <LabeledTextField disabled label="Permissions" placeholder="Select permissions" />
      )}
      <LabeledTextField
        multiline
        size="small"
        minRows={2}
        label="Justification"
        loading={isDryRunLoading}
        value={formState.justification}
        disabled={!isJustificationRequired}
        onChange={(e) => updateFormState('justification', e.target.value)}
        placeholder={isJustificationRequired ? 'Enter justification' : 'Justification not required'}
      />
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        {onClickBack && (
          <Button variant="text" onClick={onClickBack}>
            Back
          </Button>
        )}
        {templatesEnabled && (
          <TemplateCTABtn
            formState={formState}
            disable={isUsingSearchTermInResources}
            disabledReason="Unable to save to favorites when using Select All in the resources selection"
          />
        )}
        <LoadingButton
          data-trigger={AnalyticsEvents.AccessRequestSendClicked}
          variant="contained"
          loading={isLoading}
          onClick={handleRequest}
          disabled={isFormDisabled}
        >
          Request
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

function TemplateCTABtn({
  formState,
  disable,
  disabledReason,
}: {
  formState: FormState;
  disable: boolean;
  disabledReason?: string;
}) {
  if (disable)
    return (
      <Tooltip arrow placement="top" title={disabledReason || 'Unable to save to favorites'}>
        <div>
          <TemplateCTA formState={formState} disable />
        </div>
      </Tooltip>
    );

  return <TemplateCTA formState={formState} disable={false} />;
}
